import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import './SignaturePad.css';

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toDataURL: () => string | null;
}

interface Props {
  onChange?: (hasInk: boolean) => void;
  label?: string;
  height?: number;
}

/**
 * Ink colour is fixed rather than themed. The pad is always drawn as light
 * paper (see SignaturePad.css) so a signature looks the same in a dark cab as
 * it does in daylight, and the exported PNG is dark ink on transparent -
 * which is what a printed record and a PDF both need.
 */
const INK = '#141a1c';

/**
 * Finger signature capture.
 *
 * Draws with quadratic smoothing between midpoints, which keeps a fast finger
 * stroke from turning into a polygon. The backing store is scaled to the
 * device pixel ratio so the exported image is sharp enough to print.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad(
  { onChange, label = 'Sign with your finger', height = 190 },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const lastMid = useRef<{ x: number; y: number } | null>(null);
  const bounds = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);
  // Mirrors the state so `setup` can read it without changing identity - a
  // changing `setup` would leave the ResizeObserver holding a stale one that
  // clears the canvas instead of restoring it.
  const inkRef = useRef(false);
  const widthRef = useRef(0);
  const [hasInk, setHasInk] = useState(false);

  const applyContextDefaults = (ctx: CanvasRenderingContext2D, dpr: number) => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = INK;
    ctx.fillStyle = INK;
  };

  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    // Resizing the backing store wipes it, so keep any existing ink.
    const previous = inkRef.current ? canvas.toDataURL() : null;

    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(height * dpr);
    widthRef.current = rect.width;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    applyContextDefaults(ctx, dpr);

    if (previous) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, height);
      img.src = previous;
    }
  }, [height]);

  useEffect(() => {
    setup();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      // Ignore the noise a bottom-sheet animation produces; only a genuine
      // width change (rotation, split view) needs the canvas rebuilt.
      if (width > 0 && Math.abs(width - widthRef.current) > 1) setup();
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [setup]);

  /**
   * The pad is mounted inside a <dialog>, so on first render it has no box and
   * `setup` cannot size it. A ResizeObserver is not reliable across that
   * display:none transition, so the size is also checked at the moment drawing
   * starts - by then the pad is certainly on screen.
   */
  const ensureSized = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    if (canvas.width !== Math.round(rect.width * dpr) || canvas.height !== Math.round(height * dpr)) {
      setup();
    }
  }, [height, setup]);

  const pointFrom = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const markInked = () => {
    if (inkRef.current) return;
    inkRef.current = true;
    setHasInk(true);
    onChange?.(true);
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    ensureSized();
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const point = pointFrom(e);
    last.current = point;
    lastMid.current = null;
    track(point);

    // A tap with no movement should still leave a dot.
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
    markInked();
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    const previous = last.current;
    if (!ctx || !previous) return;

    const point = pointFrom(e);
    const mid = { x: (previous.x + point.x) / 2, y: (previous.y + point.y) / 2 };

    // Each segment runs midpoint-to-midpoint with the sampled point as the
    // control. Starting from the previous point instead would leave the span
    // between that midpoint and the next sample undrawn, which shows up as a
    // dashed line whenever the finger moves quickly.
    ctx.beginPath();
    ctx.moveTo(lastMid.current?.x ?? previous.x, lastMid.current?.y ?? previous.y);
    ctx.quadraticCurveTo(previous.x, previous.y, mid.x, mid.y);
    ctx.stroke();

    lastMid.current = mid;
    last.current = point;
    track(point);
  };

  const end = () => {
    // Close the stroke out to the final sample so the tail is not clipped.
    const ctx = canvasRef.current?.getContext('2d');
    if (drawing.current && ctx && lastMid.current && last.current) {
      ctx.beginPath();
      ctx.moveTo(lastMid.current.x, lastMid.current.y);
      ctx.lineTo(last.current.x, last.current.y);
      ctx.stroke();
    }
    drawing.current = false;
    last.current = null;
    lastMid.current = null;
  };

  const track = (p: { x: number; y: number }) => {
    const b = bounds.current;
    bounds.current = b
      ? {
          minX: Math.min(b.minX, p.x),
          minY: Math.min(b.minY, p.y),
          maxX: Math.max(b.maxX, p.x),
          maxY: Math.max(b.maxY, p.y),
        }
      : { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y };
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    applyContextDefaults(ctx, dpr);
    bounds.current = null;
    inkRef.current = false;
    setHasInk(false);
    onChange?.(false);
  }, [onChange]);

  useImperativeHandle(
    ref,
    () => ({
      clear,
      isEmpty: () => !inkRef.current,
      toDataURL: () => {
        const canvas = canvasRef.current;
        if (!canvas || !inkRef.current || !bounds.current) return null;

        // Crop to the ink so the stored image is the signature, not the box.
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        const pad = 8;
        const b = bounds.current;
        const sx = Math.max(0, (b.minX - pad) * dpr);
        const sy = Math.max(0, (b.minY - pad) * dpr);
        const sw = Math.min(canvas.width - sx, (b.maxX - b.minX + pad * 2) * dpr);
        const sh = Math.min(canvas.height - sy, (b.maxY - b.minY + pad * 2) * dpr);

        const out = document.createElement('canvas');
        out.width = Math.max(1, Math.round(sw));
        out.height = Math.max(1, Math.round(sh));
        const ctx = out.getContext('2d');
        if (!ctx) return canvas.toDataURL('image/png');
        ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, out.width, out.height);
        return out.toDataURL('image/png');
      },
    }),
    [clear],
  );

  return (
    <div className="sigpad">
      <div className="sigpad-frame">
        <canvas
          ref={canvasRef}
          className="sigpad-canvas"
          style={{ height }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
          aria-label={label}
          role="img"
        />
        {!hasInk && (
          <div className="sigpad-placeholder" aria-hidden="true">
            <span className="sigpad-x">✕</span>
            <span>{label}</span>
          </div>
        )}
        <div className="sigpad-rule" aria-hidden="true" />
      </div>
      <div className="row spread">
        <span className="hint">Signed records cannot be edited afterwards.</span>
        <button type="button" className="btn btn-quiet btn-sm" onClick={clear} disabled={!hasInk}>
          Clear
        </button>
      </div>
    </div>
  );
});
